import { CircularProgress } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { API_URL } from "../constants/config";
import { errorAlert, successAlert } from "../utils/toast";

const CRON_LINE =
  "* * * * * cd /var/www/backend && php artisan schedule:run >> /dev/null 2>&1";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const formatDate = (iso, timeZone) =>
  new Date(iso).toLocaleString("en-GB", {
    timeZone,
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const RunResult = ({ run, timeZone }) => {
  if (!run) return "none yet";
  if (run.status === "success") {
    return `${formatDate(run.at, timeZone)} (${run.name})`;
  }
  return (
    <span className="text-red-700">
      failed at {formatDate(run.at, timeZone)}: {run.error}
    </span>
  );
};

// Read-only: the schedule is fixed on the server (every day at midnight, Poland time)
const BackupSchedulePanel = () => {
  const [status, setStatus] = useState(null);
  const [testingR2, setTestingR2] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_URL}/admin/backups/schedule`, { headers: authHeaders() })
      .then((response) => setStatus(response.data.data))
      .catch(() => errorAlert("Failed to load the backup schedule."));
  }, []);

  const testR2 = () => {
    setTestingR2(true);
    axios
      .post(`${API_URL}/admin/backups/r2-test`, null, { headers: authHeaders() })
      .then((response) => successAlert(response.data.message))
      .catch((error) =>
        errorAlert(
          error.response?.data?.message || "Cloud storage connection failed."
        )
      )
      .finally(() => setTestingR2(false));
  };

  if (!status) {
    return (
      <div className="w-full p-4">
        <CircularProgress size={20} />
      </div>
    );
  }

  return (
    <div className="w-full border rounded-md p-4 mb-4 bg-white text-sm">
      <div className="flex justify-between items-center flex-wrap gap-2 mb-3">
        <p className="pt-sans-bold text-base">Automatic backups</p>
        <span
          className={`text-xs px-2 py-1 rounded-full ${
            status.scheduler.running
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          Scheduler: {status.scheduler.running ? "running" : "not running"}
        </span>
      </div>

      <p className="mb-3">
        The database is backed up automatically{" "}
        <span className="pt-sans-bold">
          every day at 12:00 AM (midnight), Poland time
        </span>
        {status.cloud_storage
          ? ", straight to cloud storage. Manual backups are saved there too."
          : ". Cloud storage isn't configured, so backups are saved on this server."}{" "}
        Only the newest {status.keep} backups are kept, automatic and manual
        together; older ones are deleted with each new backup.
      </p>

      {!status.scheduler.running && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-3">
          The scheduler isn't running, so automatic backups won't happen. Add
          this cron line on the server (as <code>www-data</code>):
          <code className="block mt-2 p-2 bg-white border rounded break-all">
            {CRON_LINE}
          </code>
        </div>
      )}

      <div className="space-y-2 pt-3 border-t">
        <p>
          <span className="pt-sans-bold">Next run: </span>
          {formatDate(status.upcoming_runs[0], status.timezone)}
        </p>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p>
            <span className="pt-sans-bold">Last automatic backup: </span>
            <RunResult run={status.last_run} timeZone={status.timezone} />
          </p>
          {status.cloud_storage && (
            <button
              type="button"
              className="flex items-center justify-center gap-2 px-4 py-1 text-sm border rounded-sm bg-white hover:bg-gray-50 disabled:opacity-60"
              onClick={testR2}
              disabled={testingR2}
            >
              {testingR2 && <CircularProgress size={14} color="inherit" />}
              Test cloud storage connection
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackupSchedulePanel;
